import CloseIcon from "@mui/icons-material/Close";
import { Box } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { FC, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { FONT_SIZE, INK, RADIUS } from "../../../theme/tokens";
import SystemButton from "../SystemButton/SystemButton";

export type SelectionAction = {
	/** Stable id for the React key and the derived testid. */
	id: string;
	/** Already-translated button label. */
	label: string;
	onClick: () => void;
	leadingIcon?: ReactNode;
	/** Defaults to "secondary". Use "delete" for a destructive action. */
	buttonVariant?: "secondary" | "delete" | "borderless" | "primary";
	loading?: boolean;
	/** Disable independently of the selection being empty (which already disables every action). */
	disabled?: boolean;
	/** Why the action is unavailable. A disabled bulk action with no stated reason reads as a bug. */
	tooltip?: ReactNode;
};

type SelectionActionBarProps = {
	/** Already-translated count line, e.g. "3 selected". */
	countLabel: string;
	actions: SelectionAction[];
	/** Renders a trailing Cancel that clears the selection. */
	onCancel?: () => void;
	/** Number of selected rows — every action is disabled at 0. */
	selectedCount: number;
	/**
	 * Accessible name for the group, e.g. "Bulk actions for selected employees". Required, because an
	 * unnamed group announces as an anonymous container. Must be STABLE: a name that changes with the
	 * count is re-announced on every tick, and the running total belongs in the page's own live
	 * region, not in this group's name.
	 */
	ariaLabel: string;
	dataTestId?: string;
};

/**
 * Contextual bar for acting on a multi-row selection: the live count on the left, the actions on
 * the right.
 *
 * It exists so bulk actions are visible at the moment a selection exists, rather than living in an
 * overflow menu the user has to remember — which is also why it takes its own Cancel instead of
 * relying on a "cancel selection" item somewhere else.
 */
export const SelectionActionBar: FC<SelectionActionBarProps> = ({ countLabel, actions, onCancel, selectedCount, ariaLabel, dataTestId }) => {
	const { t } = useTranslation();
	const hasSelection = selectedCount > 0;

	return (
		<Box
			data-testid={dataTestId}
			// A named group, so AT reaching these buttons announces what they belong to instead of
			// reading three unrelated buttons that appeared out of nowhere. Deliberately `group` and
			// not `toolbar`: ARIA APG expects a toolbar to be a single tab stop with arrow-key roving
			// focus, and these buttons are each independently tabbable. `group` carries no such promise.
			role="group"
			aria-label={ariaLabel}
			sx={(theme) => ({
				display: "flex",
				alignItems: "center",
				justifyContent: "space-between",
				gap: 2,
				flexWrap: "wrap",
				py: 1,
				px: 2,
				borderRadius: `${RADIUS.CARD}px`,
				backgroundColor: alpha(theme.palette.primary.main, 0.08),
				border: `1px solid ${alpha(theme.palette.primary.main, 0.24)}`,
			})}
		>
			<Box sx={{ fontSize: FONT_SIZE.EMPHASIS, fontWeight: 600, color: INK.PRIMARY }} data-testid={dataTestId ? `${dataTestId}-count` : undefined}>
				{countLabel}
			</Box>
			<Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
				{actions.map((action) => (
					<SystemButton
						key={action.id}
						data-testid={dataTestId ? `${dataTestId}-${action.id}` : undefined}
						buttonVariant={action.buttonVariant ?? "secondary"}
						disabled={!hasSelection || action.disabled}
						tooltip={action.tooltip}
						loading={action.loading}
						onClick={action.onClick}
						leadingIcon={action.leadingIcon}
						noFullWidthMobile
						sx={{ fontSize: FONT_SIZE.EMPHASIS }}
					>
						{action.label}
					</SystemButton>
				))}
				{onCancel && (
					<SystemButton
						data-testid={dataTestId ? `${dataTestId}-cancel` : undefined}
						buttonVariant="borderless"
						onClick={onCancel}
						leadingIcon={<CloseIcon sx={{ fontSize: 18 }} />}
						noFullWidthMobile
						sx={{ fontSize: FONT_SIZE.EMPHASIS }}
					>
						{t("cancel")}
					</SystemButton>
				)}
			</Box>
		</Box>
	);
};
